import type { Strain } from '@shared';
import {
  LOCAL_PRODUCTS_STORE,
  LOCAL_PRODUCT_IMAGES_BY_PRODUCT_INDEX,
  LOCAL_PRODUCT_IMAGES_STORE,
  openLocalProductsDb,
  requestToPromise,
  txDone,
  type IdbFactory,
} from './localProductsDb';
import type { ProductStore } from './types';

export {
  LOCAL_PRODUCTS_DB_NAME,
  LOCAL_PRODUCTS_DB_VERSION,
  LOCAL_PRODUCTS_STORE,
  LOCAL_PRODUCT_IMAGES_STORE,
  LOCAL_PRODUCT_IMAGES_BY_PRODUCT_INDEX,
  LOCAL_BLEND_HISTORY_STORE,
  openLocalProductsDb,
  applyLocalProductsSchemaUpgrade,
} from './localProductsDb';

/**
 * IndexedDB-backed ProductStore. Throws on storage failures rather than
 * silently dropping writes (unlike package-draft IDB helpers).
 *
 * `delete` removes the product and its `productImages` rows in one transaction
 * (`deletesImagesWithProduct: true`).
 */
export function createIdbProductStore(options?: {
  indexedDB?: IdbFactory;
}): ProductStore {
  const factory = options?.indexedDB;

  return {
    deletesImagesWithProduct: true,

    async getAll(): Promise<Strain[]> {
      const db = await openLocalProductsDb(factory);
      try {
        const tx = db.transaction(LOCAL_PRODUCTS_STORE, 'readonly');
        const store = tx.objectStore(LOCAL_PRODUCTS_STORE);
        const rows = await requestToPromise(store.getAll());
        await txDone(tx);
        return rows as Strain[];
      } finally {
        db.close();
      }
    },

    async get(id: string): Promise<Strain | undefined> {
      const db = await openLocalProductsDb(factory);
      try {
        const tx = db.transaction(LOCAL_PRODUCTS_STORE, 'readonly');
        const store = tx.objectStore(LOCAL_PRODUCTS_STORE);
        const row = await requestToPromise(store.get(id));
        await txDone(tx);
        return (row as Strain | undefined) ?? undefined;
      } finally {
        db.close();
      }
    },

    async put(strain: Strain): Promise<void> {
      const db = await openLocalProductsDb(factory);
      try {
        const tx = db.transaction(LOCAL_PRODUCTS_STORE, 'readwrite');
        const store = tx.objectStore(LOCAL_PRODUCTS_STORE);
        // Never persist transient blob: display URLs inside product rows.
        store.put({ ...strain, images: [], primaryImage: null });
        await txDone(tx);
      } finally {
        db.close();
      }
    },

    async delete(id: string): Promise<boolean> {
      const db = await openLocalProductsDb(factory);
      try {
        // Same-DB transaction: remove product + its local package photos.
        const tx = db.transaction(
          [LOCAL_PRODUCTS_STORE, LOCAL_PRODUCT_IMAGES_STORE],
          'readwrite',
        );
        const products = tx.objectStore(LOCAL_PRODUCTS_STORE);
        const images = tx.objectStore(LOCAL_PRODUCT_IMAGES_STORE);
        const existing = await requestToPromise(products.get(id));
        if (existing === undefined) {
          await txDone(tx);
          return false;
        }
        products.delete(id);
        const index = images.index(LOCAL_PRODUCT_IMAGES_BY_PRODUCT_INDEX);
        const related = (await requestToPromise(index.getAll(id))) as Array<{
          id: string;
        }>;
        for (const row of related) {
          images.delete(row.id);
        }
        await txDone(tx);
        return true;
      } finally {
        db.close();
      }
    },
  };
}
